import { useState, useEffect } from 'react'
import { Autocomplete, Box, Drawer, LinearProgress, TextField, Typography } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { search as fetchNeedsAttention } from 'store/asset/need-attention'
import { getTypes, getCampus } from 'store/lookup'
import { BasePathProvider, useSession } from 'components/Providers'
import AssignAssetFormIndex from '../MyDevice/Form'
import AcceptDraftForm from './AcceptDraftForm'
import AssetFormSkeleton from '../MyDevice/Form/FormSekeleton'
import RecentlyAdded from './RecentlyAdded'
import DevicesToAccept from './DevicesToAccept'
import InactiveAssigned from './InactiveAssigned'

const basePath = '/asset-management/needs-attention'

export default function NeedsAttentionPage() {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { id } = useParams()
  const location = useLocation()
  const { district } = useSession()
  const action = location.state?.action
  const [expanded, setExpanded] = useState()
  const loading = useSelector(state => state.asset.needsAttention.loading)
  const recentlyAdded = useSelector(state => state.asset.needsAttention.recentlyAdded)
  const draftAsset = action === 'accept' ? recentlyAdded?.find(item => item.id === Number(id)) ?? null : null
  const types = useSelector(state => state.lookup.types)
  const campus = useSelector(state => state.lookup.campus?.filter(c => ['provider', 'school'].includes(c.type)))

  const assetType = types?.filter(t => t.category === 'assetType') || []
  const assetCondition = types?.filter(t => t.category === 'assetConditionType') || []

  const defaultCampus = campus?.find(c => c.name?.includes('iLearn')) || null
  const [campusId, setCampusId] = useState(undefined)

  useEffect(() => {
    if (campus && campusId === undefined) {
      setCampusId(defaultCampus?.id ?? null)
    }
  }, [Boolean(campus)])

  useEffect(() => {
    if (campusId === undefined) return
    dispatch(fetchNeedsAttention(campusId ? { campusId } : {}))
  }, [dispatch, campusId])

  useEffect(() => {
    if (!types) dispatch(getTypes())
  }, [dispatch, Boolean(types)])

  useEffect(() => {
    if (!campus) dispatch(getCampus())
  }, [dispatch, Boolean(campus)])

  const handleChange = panel => (_, isExpanded) => setExpanded(isExpanded ? panel : false)

  return (
    <BasePathProvider value={basePath}>
      <Box p={2}>
        <Typography variant="h6" gutterBottom fontWeight={600}>{t('Review')}</Typography>
        <Typography variant="body2" color="text.secondary" mb={3}>
          {t('Review devices that require attention and take necessary actions')}
        </Typography>
        {district?.isServiceProvider && (
          <Autocomplete
            size="small"
            sx={{ maxWidth: 320, mb: 3 }}
            options={campus || []}
            getOptionLabel={c => c.name}
            value={campus?.find(c => c.id === campusId) || null}
            onChange={(_, val) => setCampusId(val?.id ?? null)}
            renderInput={params => <TextField {...params} label={t('Campus')} />}
          />
        )}
        {loading ? (
          <LinearProgress />
        ) : (
          <>
            <RecentlyAdded expanded={expanded === 'panel1'} onChange={handleChange('panel1')} showCampus={district?.isServiceProvider && !campusId} />
            <DevicesToAccept expanded={expanded === 'panel2'} onChange={handleChange('panel2')} />
            <InactiveAssigned expanded={expanded === 'panel3'} onChange={handleChange('panel3')} showCampus={district?.isServiceProvider && !campusId} />
          </>
        )}
      </Box>

      <Drawer
        PaperProps={{ sx: { width: 800 } }}
        open={Boolean(id)}
        anchor="right"
        onClose={() => navigate(basePath)}
      >
        {action === 'accept'
          ? draftAsset
            ? <AcceptDraftForm asset={draftAsset} assetType={assetType} assetCondition={assetCondition} />
            : <AssetFormSkeleton />
          : <AssignAssetFormIndex assetType={assetType} assetCondition={assetCondition} campus={campus} />
        }
      </Drawer>
    </BasePathProvider>
  )
}
